import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type Paginated, type ApiSuccess } from '../../lib/api';
import { PERMISSION_GROUPS, ALL_PERMISSIONS } from '@img/shared';

interface UserRow {
  id: string;
  userNumber: number;
  name: string;
  email: string | null;
  phone: string | null;
  role: 'USER' | 'BROKER' | 'ADMIN' | 'SUPER_ADMIN';
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED';
  verified: boolean;
  verificationTier: string | null;
  walletBalance: number | string;
  trustedBidder: boolean;
  totalLogins: number;
  lastLoginAt: string | null;
  createdAt: string;
}

interface UserDetail extends UserRow {
  userNumber: number;
  emailVerified: boolean;
  phoneVerified: boolean;
  city: string | null;
  bio: string | null;
  walletHeld: number | string;
  customPermissions: string[] | null;
  lastLoginIp: string | null;
  // 🏷️ خصائص موسّعة
  adminNote: string | null;
  customBadge: string | null;
  customBadgeColor: string | null;
  bidCap: number | string | null;
  blacklisted: boolean;
  invitedById: string | null;
  invitedAt: string | null;
  brokerProfile: null | {
    subscriptionPlan: string;
    subscriptionExpiresAt: string | null;
    subscriptionDiscountPct: number | string | null;
    subscriptionDiscountFixed: number | string | null;
    subscriptionDiscountUntil: string | null;
    subscriptionDiscountReason: string | null;
  };
}

const ROLE_COLOR: Record<string, 'navy' | 'gold' | 'green'> = {
  USER: 'navy', BROKER: 'green', ADMIN: 'gold', SUPER_ADMIN: 'gold',
};
const STATUS_COLOR: Record<string, 'green' | 'amber' | 'red'> = {
  ACTIVE: 'green', SUSPENDED: 'amber', DELETED: 'red',
};

export function AdminUsersPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ q: '', role: '', status: '', tier: '', page: 1 });
  const [openId, setOpenId] = useState<string | null>(null);
  const [openCreate, setOpenCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', filters],
    queryFn: () => api.get<Paginated<UserRow>>('/api/admin/users', { query: filters }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-black text-navy-dark">إدارة المستخدمين</h1>
        <Button variant="primary" onClick={() => setOpenCreate(true)}>+ إضافة مستخدم</Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input placeholder="بحث (اسم/إيميل/جوال)…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })} />
            <select value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value, page: 1 })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
              <option value="">كل الأدوار</option>
              <option value="USER">عميل</option>
              <option value="BROKER">مسوّق</option>
              <option value="ADMIN">إداري</option>
              <option value="SUPER_ADMIN">سوبر أدمن</option>
            </select>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
              <option value="">كل الحالات</option>
              <option value="ACTIVE">نشط</option>
              <option value="SUSPENDED">موقوف</option>
              <option value="DELETED">محذوف</option>
            </select>
            <select value={filters.tier} onChange={(e) => setFilters({ ...filters, tier: e.target.value, page: 1 })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
              <option value="">كل المستويات</option>
              <option value="NONE">بدون توثيق</option>
              <option value="GREEN">أخضر</option>
              <option value="GOLD">ذهبي</option>
              <option value="DIAMOND">ماسي</option>
            </select>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>المستخدم</Th>
                <Th>الدور</Th>
                <Th>الحالة</Th>
                <Th>التوثيق</Th>
                <Th>المحفظة</Th>
                <Th>دخولات</Th>
                <Th>إجراءات</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-500">⟳ جاري التحميل…</td></tr>
              ) : !data?.data?.length ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-500">لا يوجد مستخدمون</td></tr>
              ) : data.data.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setOpenId(u.id)}>
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded">#{u.userNumber}</span>
                      <span className="font-bold">{u.name}</span>
                    </div>
                    <div className="text-xs text-gray-500" dir="ltr">{u.email || u.phone}</div>
                  </Td>
                  <Td><Badge color={ROLE_COLOR[u.role]}>{u.role}</Badge></Td>
                  <Td><Badge color={STATUS_COLOR[u.status]}>{u.status}</Badge></Td>
                  <Td>
                    {u.verificationTier ? <Badge color="green">{u.verificationTier}</Badge> : <span className="text-xs text-gray-400">—</span>}
                  </Td>
                  <Td className="font-bold">{Number(u.walletBalance).toLocaleString('en-US')} ر.س</Td>
                  <Td>{u.totalLogins}</Td>
                  <Td><Button size="sm" variant="primary">تفاصيل</Button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {data && data.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>السابق</Button>
          <span className="px-4 py-2 text-sm font-bold">{filters.page} / {data.pagination.totalPages}</span>
          <Button size="sm" variant="outline" disabled={filters.page >= data.pagination.totalPages} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>التالي</Button>
        </div>
      )}

      {openId && (
        <UserDetailModal
          userId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })}
        />
      )}

      {openCreate && (
        <CreateUserModal
          onClose={() => setOpenCreate(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })}
        />
      )}
    </div>
  );
}

function UserDetailModal({ userId, onClose, onChanged }: { userId: string; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => api.get<ApiSuccess<UserDetail>>(`/api/admin/users/${userId}`),
  });

  const [tab, setTab] = useState<'info' | 'permissions' | 'wallet' | 'advanced' | 'broker' | 'security' | 'danger'>('info');

  if (isLoading || !data?.data) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-gray-500">⟳ جاري التحميل…</div>
      </div>
    );
  }
  const u = data.data;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-navy-dark text-white flex items-center justify-center text-2xl">👤</div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-black text-xl text-navy-dark">{u.name}</h2>
                <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-1 rounded">#{u.userNumber}</span>
              </div>
              <p className="text-sm text-gray-500" dir="ltr">{u.email || u.phone}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>

        <div className="flex gap-2 px-5 pt-3 border-b border-gray-200 overflow-x-auto">
          <TabBtn active={tab === 'info'} onClick={() => setTab('info')}>📋 المعلومات والدور</TabBtn>
          <TabBtn active={tab === 'permissions'} onClick={() => setTab('permissions')}>🔑 الصلاحيات</TabBtn>
          <TabBtn active={tab === 'wallet'} onClick={() => setTab('wallet')}>💰 المحفظة</TabBtn>
          <TabBtn active={tab === 'advanced'} onClick={() => setTab('advanced')}>🏷️ خصائص متقدمة</TabBtn>
          {u.role === 'BROKER' && <TabBtn active={tab === 'broker'} onClick={() => setTab('broker')}>🎁 خصم اشتراك</TabBtn>}
          <TabBtn active={tab === 'security'} onClick={() => setTab('security')}>🔐 الأمان</TabBtn>
          <TabBtn active={tab === 'danger'} onClick={() => setTab('danger')}>⚠️ منطقة الخطر</TabBtn>
        </div>

        <div className="p-5">
          {tab === 'info' && <InfoTab user={u} onChanged={() => { qc.invalidateQueries({ queryKey: ['admin', 'user', userId] }); onChanged(); }} />}
          {tab === 'permissions' && <PermissionsTab user={u} onChanged={() => { qc.invalidateQueries({ queryKey: ['admin', 'user', userId] }); onChanged(); }} />}
          {tab === 'wallet' && <WalletTab user={u} onChanged={() => { qc.invalidateQueries({ queryKey: ['admin', 'user', userId] }); onChanged(); }} />}
          {tab === 'advanced' && <AdvancedTab user={u} onChanged={() => { qc.invalidateQueries({ queryKey: ['admin', 'user', userId] }); onChanged(); }} />}
          {tab === 'broker' && u.role === 'BROKER' && <BrokerDiscountTab user={u} onChanged={() => { qc.invalidateQueries({ queryKey: ['admin', 'user', userId] }); onChanged(); }} />}
          {tab === 'security' && <SecurityTab user={u} />}
          {tab === 'danger' && <DangerTab user={u} onChanged={onChanged} onClose={onClose} />}
        </div>
      </div>
    </div>
  );
}

function InfoTab({ user, onChanged }: { user: UserDetail; onChanged: () => void }) {
  const [role, setRole] = useState(user.role);
  const [status, setStatus] = useState(user.status);
  const [tier, setTier] = useState(user.verificationTier ?? 'NONE');
  const [verified, setVerified] = useState(user.verified);
  const [trustedBidder, setTrustedBidder] = useState(user.trustedBidder);

  const updateMutation = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${user.id}`, { role, status, verificationTier: tier === 'NONE' ? null : tier, verified, trustedBidder }),
    onSuccess: onChanged,
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="الإيميل" value={user.email ?? '—'} dir="ltr" extra={user.emailVerified && <Badge color="green">✓</Badge>} />
        <Field label="الجوال" value={user.phone ?? '—'} dir="ltr" extra={user.phoneVerified && <Badge color="green">✓</Badge>} />
        <Field label="المدينة" value={user.city ?? '—'} />
        <Field label="عدد الدخولات" value={String(user.totalLogins)} />
        {user.lastLoginAt && <Field label="آخر دخول" value={new Date(user.lastLoginAt).toLocaleString('ar-SA')} />}
        {user.lastLoginIp && <Field label="آخر IP" value={user.lastLoginIp} dir="ltr" />}
      </div>

      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">⚙️ التحكم</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <label className="block text-sm font-bold text-ink mb-1">الدور</label>
            <select value={role} onChange={(e) => setRole(e.target.value as never)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
              <option value="USER">عميل (USER)</option>
              <option value="BROKER">مسوّق (BROKER)</option>
              <option value="ADMIN">إداري (ADMIN)</option>
              <option value="SUPER_ADMIN">سوبر أدمن (SUPER_ADMIN)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">الحالة</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as never)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
              <option value="ACTIVE">نشط</option>
              <option value="SUSPENDED">موقوف</option>
              <option value="DELETED">محذوف</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">مستوى التوثيق</label>
            <select value={tier} onChange={(e) => setTier(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
              <option value="NONE">بدون</option>
              <option value="GREEN">أخضر</option>
              <option value="GOLD">ذهبي</option>
              <option value="DIAMOND">ماسي</option>
            </select>
          </div>
          <label className="flex items-center gap-2 mt-7">
            <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} className="w-5 h-5 accent-green-600" />
            <span className="text-sm font-bold">حساب موثّق إجمالاً</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={trustedBidder} onChange={(e) => setTrustedBidder(e.target.checked)} className="w-5 h-5 accent-amber-600" />
            <span className="text-sm font-bold">⭐ مزايد موثوق (يزايد بدون عربون)</span>
          </label>
        </div>
      </fieldset>

      <div className="flex justify-end">
        <Button variant="primary" onClick={() => updateMutation.mutate()} loading={updateMutation.isPending}>💾 حفظ التغييرات</Button>
      </div>
    </div>
  );
}

function PermissionsTab({ user, onChanged }: { user: UserDetail; onChanged: () => void }) {
  // 🛡️ تطبيع البيانات — قد تأتي customPermissions كـ null أو string أو array من الـ Json column
  const normalizePermissions = (raw: unknown): string[] => {
    if (Array.isArray(raw)) return raw.filter((x) => typeof x === 'string');
    if (typeof raw === 'string') {
      try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
    }
    return [];
  };

  const [perms, setPerms] = useState<Set<string>>(() => new Set(normalizePermissions(user.customPermissions)));
  const [savedSnapshot, setSavedSnapshot] = useState<Set<string>>(() => new Set(normalizePermissions(user.customPermissions)));
  const isFullAdmin = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';

  // 🔄 لما البيانات تتحدّث من الخادم (بعد invalidate)، نسيـنك الـ state
  useEffect(() => {
    const fresh = new Set(normalizePermissions(user.customPermissions));
    setPerms(fresh);
    setSavedSnapshot(fresh);
  }, [user.id, user.customPermissions]);

  const updateMutation = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${user.id}/permissions`, { permissions: Array.from(perms) }),
    onSuccess: () => {
      setSavedSnapshot(new Set(perms));
      onChanged();
    },
  });

  const toggle = (key: string) => setPerms((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key);
    else n.add(key);
    return n;
  });

  const toggleGroup = (groupKeys: string[], on: boolean) => setPerms((s) => {
    const n = new Set(s);
    if (on) groupKeys.forEach((k) => n.add(k));
    else groupKeys.forEach((k) => n.delete(k));
    return n;
  });

  // 🧮 الفرق بين المحفوظ والحالي
  const granted = Array.from(perms).filter((k) => !savedSnapshot.has(k));
  const revoked = Array.from(savedSnapshot).filter((k) => !perms.has(k));
  const hasChanges = granted.length > 0 || revoked.length > 0;

  if (isFullAdmin) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="font-black text-amber-900">👑 هذا المستخدم {user.role}</div>
        <p className="text-sm text-amber-800 mt-1">لديه كل الصلاحيات تلقائياً ولا يحتاج صلاحيات مخصّصة.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 📊 ملخص الصلاحيات الحالية المحفوظة */}
      <div className="bg-gradient-to-l from-navy-soft to-blue-50 border-2 border-navy/20 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-black text-navy-dark text-sm">📋 الصلاحيات المحفوظة حالياً</h3>
          <Badge color="navy">{savedSnapshot.size} / {ALL_PERMISSIONS.length}</Badge>
        </div>
        {savedSnapshot.size === 0 ? (
          <p className="text-xs text-gray-500">لا يملك أي صلاحيات مخصّصة بعد. استخدم النموذج أدناه لمنحه صلاحيات.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {Array.from(savedSnapshot).map((k) => {
              const p = ALL_PERMISSIONS.find((x) => x.key === k);
              return (
                <span key={k} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-navy/20 text-[11px] font-bold text-navy-dark">
                  ✓ {p?.label ?? k}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-800">
        🔑 الصلاحيات المخصّصة تُمنح لمستخدمين عاديين/مسوّقين لإعطائهم قدرات محدّدة (مثل المحرّج).
        مفعّلة الآن (بعد التعديلات): <strong>{perms.size}</strong> من {ALL_PERMISSIONS.length}.
        {hasChanges && (
          <div className="mt-2 text-xs">
            🔄 تغييرات غير محفوظة: <span className="text-green-700 font-bold">+{granted.length}</span> {' '}
            <span className="text-red-700 font-bold">-{revoked.length}</span>
            {' — '}عند الحفظ سيتم إرسال إيميل للمستخدم بتفاصيل الصلاحيات الجديدة ومسؤولياتها.
          </div>
        )}
      </div>

      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        {PERMISSION_GROUPS.map((group) => {
          const groupKeys = group.permissions.map((p) => p.key);
          const allOn = groupKeys.every((k) => perms.has(k));
          const someOn = groupKeys.some((k) => perms.has(k));
          return (
            <fieldset key={group.key} className="border border-gray-200 rounded-xl p-4">
              <legend className="px-2 flex items-center gap-2">
                <span className="font-black text-navy-dark text-sm">{group.label}</span>
                <button
                  type="button"
                  onClick={() => toggleGroup(groupKeys, !allOn)}
                  className={`text-xs px-2 py-0.5 rounded-full font-bold transition ${
                    allOn ? 'bg-green-600 text-white' : someOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {allOn ? '✓ الكل' : someOn ? `${groupKeys.filter((k) => perms.has(k)).length}/${groupKeys.length}` : 'تفعيل الكل'}
                </button>
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {group.permissions.map((p) => {
                  const on = perms.has(p.key);
                  return (
                    <label key={p.key} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition ${
                      on ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'
                    }`}>
                      <input type="checkbox" checked={on} onChange={() => toggle(p.key)} className="w-4 h-4 accent-green-600" />
                      <span className="text-xs font-bold text-ink flex-1">{p.label}</span>
                      <code className="text-[10px] text-gray-400">{p.key}</code>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          );
        })}
      </div>

      <div className="flex flex-wrap justify-end gap-2 sticky bottom-0 bg-white pt-3 border-t border-gray-200">
        <Button variant="ghost" onClick={() => setPerms(new Set(savedSnapshot))} disabled={!hasChanges}>↶ تراجع</Button>
        <Button variant="ghost" onClick={() => setPerms(new Set())}>مسح الكل</Button>
        <Button
          variant="primary"
          onClick={() => updateMutation.mutate()}
          loading={updateMutation.isPending}
          disabled={!hasChanges}
        >
          💾 حفظ الصلاحيات {hasChanges && `(${granted.length + revoked.length})`}
        </Button>
      </div>
      {updateMutation.isSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
          ✓ تم حفظ الصلاحيات وإرسال إيميل للمستخدم بتفاصيل التغييرات والمسؤوليات.
        </div>
      )}
    </div>
  );
}

function WalletTab({ user, onChanged }: { user: UserDetail; onChanged: () => void }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const adjust = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ newBalance: number }>>(`/api/admin/users/${user.id}/wallet-adjust`, { amount: Number(amount), reason }),
    onSuccess: (res) => { setDone(`الرصيد الجديد: ${res.data.newBalance.toLocaleString('en-US')} ر.س`); setAmount(''); setReason(''); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });

  const balance = Number(user.walletBalance);
  const held = Number(user.walletHeld);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <Card><CardBody><div className="text-xs text-gray-500">الرصيد</div><div className="text-2xl font-black text-navy-dark">{balance.toLocaleString('en-US')}</div></CardBody></Card>
        <Card><CardBody><div className="text-xs text-gray-500">المحجوز</div><div className="text-2xl font-black text-amber-600">{held.toLocaleString('en-US')}</div></CardBody></Card>
        <Card><CardBody><div className="text-xs text-gray-500">المتاح</div><div className="text-2xl font-black text-green-600">{(balance - held).toLocaleString('en-US')}</div></CardBody></Card>
      </div>

      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">💰 تعديل الرصيد</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <Input label="المبلغ (موجب = إيداع، سالب = خصم)" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Input label="السبب *" value={reason} onChange={(e) => setReason(e.target.value)} required hint="يظهر في سجل المعاملات" />
        </div>
        {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mt-3">{err}</div>}
        {done && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg p-3 mt-3">✓ {done}</div>}
        <div className="flex justify-end mt-3">
          <Button variant="primary" onClick={() => { setBusy(true); setErr(null); setDone(null); adjust.mutate(undefined, { onSettled: () => setBusy(false) }); }} loading={busy} disabled={!amount || !reason}>
            تنفيذ التعديل
          </Button>
        </div>
      </fieldset>
    </div>
  );
}

// 🏷️ تاب الخصائص المتقدمة
function AdvancedTab({ user, onChanged }: { user: UserDetail; onChanged: () => void }) {
  const [adminNote, setAdminNote] = useState(user.adminNote ?? '');
  const [customBadge, setCustomBadge] = useState(user.customBadge ?? '');
  const [customBadgeColor, setCustomBadgeColor] = useState(user.customBadgeColor ?? 'navy');
  const [bidCap, setBidCap] = useState(user.bidCap?.toString() ?? '');
  const [blacklisted, setBlacklisted] = useState(user.blacklisted);

  const m = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${user.id}`, {
      adminNote: adminNote.trim() || null,
      customBadge: customBadge.trim() || null,
      customBadgeColor: customBadge.trim() ? customBadgeColor : null,
      bidCap: bidCap ? Number(bidCap) : null,
      blacklisted,
    }),
    onSuccess: onChanged,
  });

  return (
    <div className="space-y-5">
      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">📝 ملاحظات إدارية (داخلية)</legend>
        <textarea
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
          rows={4}
          placeholder="ملاحظات لا يراها المستخدم — تظهر للإدارة فقط"
          className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
        />
      </fieldset>

      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">🏆 شارة مخصّصة</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <Input label="نص الشارة" value={customBadge} onChange={(e) => setCustomBadge(e.target.value)} placeholder="مثل: VIP, شريك" />
          <div>
            <label className="block text-sm font-bold text-ink mb-1">لون الشارة</label>
            <select value={customBadgeColor} onChange={(e) => setCustomBadgeColor(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
              <option value="gold">🟡 ذهبي</option>
              <option value="green">🟢 أخضر</option>
              <option value="red">🔴 أحمر</option>
              <option value="navy">🔵 كحلي</option>
              <option value="amber">🟠 برتقالي</option>
            </select>
          </div>
        </div>
        {customBadge && (
          <div className="mt-3 text-sm">
            <span className="text-gray-500 ml-2">معاينة:</span>
            <Badge color={customBadgeColor as never}>{customBadge}</Badge>
          </div>
        )}
      </fieldset>

      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">🎯 قيود المزايدة</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <Input
            label="الحد الأعلى للمزايدة (ر.س)"
            type="number"
            value={bidCap}
            onChange={(e) => setBidCap(e.target.value)}
            placeholder="بدون = غير محدد"
            hint="لن يقدر يزايد بمبلغ أعلى من هذا"
          />
          <label className="flex items-center gap-2 mt-7">
            <input type="checkbox" checked={blacklisted} onChange={(e) => setBlacklisted(e.target.checked)} className="w-5 h-5 accent-red-600" />
            <span className="text-sm font-bold text-red-700">🚫 محظور من كل المزادات (Blacklist)</span>
          </label>
        </div>
      </fieldset>

      <div className="flex justify-end">
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>💾 حفظ الخصائص</Button>
      </div>
    </div>
  );
}

// 🎁 تاب خصم اشتراك المسوّق
function BrokerDiscountTab({ user, onChanged }: { user: UserDetail; onChanged: () => void }) {
  const bp = user.brokerProfile;
  const [pct, setPct] = useState(bp?.subscriptionDiscountPct?.toString() ?? '');
  const [fixed, setFixed] = useState(bp?.subscriptionDiscountFixed?.toString() ?? '');
  const [until, setUntil] = useState(bp?.subscriptionDiscountUntil ? bp.subscriptionDiscountUntil.slice(0, 10) : '');
  const [reason, setReason] = useState(bp?.subscriptionDiscountReason ?? '');

  const m = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${user.id}/broker-discount`, {
      pct: pct ? Number(pct) : null,
      fixed: fixed ? Number(fixed) : null,
      untilDate: until ? new Date(until).toISOString() : null,
      reason: reason.trim() || null,
    }),
    onSuccess: onChanged,
  });

  if (!bp) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        لا يوجد ملف مسوّق لهذا المستخدم بعد. يجب إنشاء ملف مسوّق أولاً.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm">
        <div><b>الخطة الحالية:</b> {bp.subscriptionPlan}</div>
        {bp.subscriptionExpiresAt && <div><b>تنتهي في:</b> {new Date(bp.subscriptionExpiresAt).toLocaleDateString('ar-SA')}</div>}
      </div>

      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">🎁 طبّق خصماً (يحلّ مكان أي خصم سابق)</legend>
        <p className="text-xs text-gray-500 mt-2">
          يُطبَّق على رسوم تجديد اشتراك المسوّق. ضع نسبة مئوية أو مبلغ ثابت (واحد فقط أو الاثنين).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <Input label="نسبة الخصم %" type="number" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="مثل: 25" />
          <Input label="خصم ثابت (ر.س)" type="number" value={fixed} onChange={(e) => setFixed(e.target.value)} placeholder="مثل: 100" />
          <Input label="ساري حتى" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
          <Input label="سبب الخصم" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="عرض ترويجي، شراكة..." />
        </div>
      </fieldset>

      <div className="flex justify-between gap-2">
        <Button variant="ghost" onClick={() => { setPct(''); setFixed(''); setUntil(''); setReason(''); m.mutate(); }}>إلغاء الخصم</Button>
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>💾 حفظ الخصم</Button>
      </div>
    </div>
  );
}

// 🔐 تاب الأمان — إعادة تعيين كلمة المرور
function SecurityTab({ user }: { user: UserDetail }) {
  const [mode, setMode] = useState<'auto' | 'manual'>('auto');
  const [pwd, setPwd] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [result, setResult] = useState<{ newPassword: string; emailSent: boolean } | null>(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const m = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ newPassword: string; emailSent: boolean }>>(`/api/admin/users/${user.id}/reset-password`, {
      newPassword: mode === 'manual' ? pwd : undefined,
      sendEmail,
    }),
    onSuccess: (r) => { setResult(r.data); setErr(''); },
    onError: (e: Error) => setErr(e.message),
  });

  const copy = async () => {
    if (!result?.newPassword) return;
    try { await navigator.clipboard.writeText(result.newPassword); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* */ }
  };

  if (result) {
    return (
      <div className="space-y-4">
        <div className={`rounded-xl p-5 border-2 ${result.emailSent ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'}`}>
          <div className="text-3xl mb-2">{result.emailSent ? '✅' : '⚠️'}</div>
          <h3 className="font-black text-lg text-navy-dark mb-1">
            {result.emailSent ? 'تم إعادة التعيين وإرسال الإيميل' : 'تم إعادة التعيين'}
          </h3>
          <p className="text-sm text-gray-700 mb-4">
            {result.emailSent
              ? `أرسلنا كلمة المرور الجديدة لـ ${user.email}. كل جلسات المستخدم أُلغيت — سيُجبَر على تسجيل دخول جديد.`
              : 'انسخ كلمة المرور أدناه وأرسلها للمستخدم بنفسك. كل جلساته أُلغيت.'}
          </p>
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="text-xs text-gray-500 mb-1">كلمة المرور الجديدة:</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono font-bold text-lg text-navy-dark" dir="ltr">{result.newPassword}</code>
              <Button size="sm" variant={copied ? 'primary' : 'ghost'} onClick={copy}>
                {copied ? '✓ نُسخ' : '📋 نسخ'}
              </Button>
            </div>
          </div>
        </div>
        <Button variant="ghost" onClick={() => { setResult(null); setPwd(''); setMode('auto'); }}>← إعادة تعيين مرة أخرى</Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <fieldset className="border-2 border-navy/30 rounded-xl p-4 bg-navy-soft/20">
        <legend className="px-2 font-black text-navy-dark">🔐 إعادة تعيين كلمة المرور</legend>
        <p className="text-sm text-gray-700 mt-1 leading-relaxed">
          يلغي كل جلسات المستخدم النشطة ويعيّن كلمة مرور جديدة. أنسب لما يفقد المستخدم كلمته أو يحتاج تجديدها.
        </p>

        <div className="flex gap-2 mt-4">
          <button type="button" onClick={() => setMode('auto')} className={`flex-1 p-3 rounded-xl border-2 font-bold transition text-sm ${mode === 'auto' ? 'border-navy bg-navy text-white' : 'border-gray-200 bg-white text-gray-700'}`}>
            🎲 توليد كلمة عشوائية
          </button>
          <button type="button" onClick={() => setMode('manual')} className={`flex-1 p-3 rounded-xl border-2 font-bold transition text-sm ${mode === 'manual' ? 'border-navy bg-navy text-white' : 'border-gray-200 bg-white text-gray-700'}`}>
            ✏️ كلمة مخصّصة
          </button>
        </div>

        {mode === 'manual' && (
          <div className="mt-3">
            <Input label="كلمة المرور الجديدة (٨ أحرف فأكثر)" value={pwd} onChange={(e) => setPwd(e.target.value)} dir="ltr" type="text" />
          </div>
        )}

        <label className="flex items-center gap-2 mt-4 cursor-pointer">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-5 h-5 accent-navy" />
          <span className="text-sm font-bold">
            📧 إرسال كلمة المرور بالإيميل {user.email && <span className="text-gray-500" dir="ltr">({user.email})</span>}
          </span>
        </label>
        {!user.email && sendEmail && (
          <div className="text-xs text-amber-700 mt-1">⚠️ لا يوجد بريد إلكتروني للمستخدم — لن يُرسَل الإيميل حتى لو فعّلت الخيار</div>
        )}

        {err && <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}

        <div className="flex justify-end mt-4">
          <Button
            variant="primary"
            onClick={() => m.mutate()}
            loading={m.isPending}
            disabled={mode === 'manual' && pwd.length < 8}
          >
            🔑 إعادة التعيين
          </Button>
        </div>
      </fieldset>

      <fieldset className="border-2 border-gray-200 rounded-xl p-4 bg-gray-50">
        <legend className="px-2 font-black text-gray-700">💡 بديل</legend>
        <p className="text-sm text-gray-600 mt-1 leading-relaxed">
          إذا كان المستخدم يعرف بريده ويقدر يفتحه، خلّه يضغط <b>"نسيت كلمة المرور"</b> من صفحة الدخول. النظام بيرسل له رابط آمن مباشرة.
        </p>
      </fieldset>
    </div>
  );
}

// ⚠️ تاب الحذف
function DangerTab({ user, onChanged, onClose }: { user: UserDetail; onChanged: () => void; onClose: () => void }) {
  const [confirmText, setConfirmText] = useState('');
  const [hardConfirm, setHardConfirm] = useState('');

  const softDelete = useMutation({
    mutationFn: () => api.delete(`/api/admin/users/${user.id}`),
    onSuccess: () => { onChanged(); onClose(); },
  });

  const hardDelete = useMutation({
    mutationFn: () => api.delete(`/api/admin/users/${user.id}/hard?confirm=PERMANENT_DELETE`),
    onSuccess: () => { onChanged(); onClose(); },
  });

  return (
    <div className="space-y-6">
      <fieldset className="border-2 border-amber-300 rounded-xl p-4 bg-amber-50">
        <legend className="px-2 font-black text-amber-900">🟡 حذف ناعم (يُستحسن)</legend>
        <p className="text-sm text-amber-800 mt-1">
          تغيّر حالة المستخدم إلى DELETED ويُمنع من الدخول، لكن تبقى بياناته وعلاقاته (مزادات، عقارات، سجلات) محفوظة. يمكن إعادة تفعيله لاحقاً.
        </p>
        <div className="mt-3">
          <Input label={`اكتب "حذف ${user.name}" للتأكيد`} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
        </div>
        <div className="flex justify-end mt-3">
          <Button
            variant="primary"
            onClick={() => softDelete.mutate()}
            loading={softDelete.isPending}
            disabled={confirmText !== `حذف ${user.name}`}
          >
            🗑️ حذف ناعم
          </Button>
        </div>
      </fieldset>

      <fieldset className="border-2 border-red-400 rounded-xl p-4 bg-red-50">
        <legend className="px-2 font-black text-red-900">🔴 حذف نهائي (SUPER_ADMIN فقط)</legend>
        <p className="text-sm text-red-800 mt-1">
          ⚠️ يمسح المستخدم وكل سجلاته من القاعدة. لا يمكن التراجع. يكسر السجلات التاريخية المرتبطة (Cascade).
        </p>
        <div className="mt-3">
          <Input label='اكتب "PERMANENT_DELETE" للتأكيد' value={hardConfirm} onChange={(e) => setHardConfirm(e.target.value)} dir="ltr" />
        </div>
        <div className="flex justify-end mt-3">
          <Button
            variant="primary"
            className="!bg-red-700 hover:!bg-red-800"
            onClick={() => hardDelete.mutate()}
            loading={hardDelete.isPending}
            disabled={hardConfirm !== 'PERMANENT_DELETE'}
          >
            💀 حذف نهائي
          </Button>
        </div>
      </fieldset>
    </div>
  );
}

// ➕ مودال إضافة مستخدم جديد
function CreateUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'USER' as 'USER' | 'BROKER' | 'ADMIN' | 'SUPER_ADMIN',
    verificationTier: 'NONE' as 'NONE' | 'GREEN' | 'GOLD' | 'DIAMOND',
    city: '',
    bio: '',
  });
  const [err, setErr] = useState('');

  const m = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ id: string }>>('/api/admin/users', {
      ...form,
      verificationTier: form.verificationTier === 'NONE' ? undefined : form.verificationTier,
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
    }),
    onSuccess: () => { onCreated(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">➕ إضافة مستخدم جديد</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="الاسم *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input label="كلمة مرور مؤقتة *" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} hint="8 أحرف فأكثر" />
            <Input label="الإيميل" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} dir="ltr" />
            <Input label="الجوال" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} dir="ltr" placeholder="+9665XXXXXXXX" />
            <div>
              <label className="block text-sm font-bold text-ink mb-1">الدور *</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as never })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                <option value="USER">عميل (USER)</option>
                <option value="BROKER">مسوّق (BROKER)</option>
                <option value="ADMIN">إداري (ADMIN)</option>
                <option value="SUPER_ADMIN">سوبر أدمن (SUPER_ADMIN)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-1">مستوى التوثيق</label>
              <select value={form.verificationTier} onChange={(e) => setForm({ ...form, verificationTier: e.target.value as never })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                <option value="NONE">بدون</option>
                <option value="GREEN">أخضر</option>
                <option value="GOLD">ذهبي</option>
                <option value="DIAMOND">ماسي</option>
              </select>
            </div>
            <Input label="المدينة" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div className="text-xs text-gray-500">يجب إدخال الإيميل أو الجوال على الأقل.</div>
          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="ghost" onClick={onClose}>إلغاء</Button>
            <Button
              variant="primary"
              onClick={() => m.mutate()}
              loading={m.isPending}
              disabled={!form.name || !form.password || (!form.email && !form.phone)}
            >
              ✓ إنشاء المستخدم
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, dir, extra }: { label: string; value: string; dir?: string; extra?: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-bold text-ink flex items-center gap-2" dir={dir}>{value} {extra}</div>
    </div>
  );
}

function TabBtn({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500 hover:text-navy-dark'
    }`}>{children}</button>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
